import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from '@/components/ui/carousel';
import { Swords, Zap, Clock, ArrowRight, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useMyChallengeSuggestions, MY_CHALLENGES_KEY, GroupChallengeGroup } from '@/hooks/useMyChallengeSuggestions';
import { ChallengeMatch, PairInfo } from '@/types/challengeMatch';

const getInitials = (name?: string | null) =>
  (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

const PairAvatars: React.FC<{ pair?: PairInfo }> = ({ pair }) => (
  <div className="flex items-center gap-1.5 min-w-0">
    <div className="flex -space-x-2 shrink-0">
      <Avatar className="h-7 w-7 border-2 border-background">
        <AvatarImage src={pair?.player1?.avatar_url || undefined} />
        <AvatarFallback className="text-[10px] bg-primary/10">{getInitials(pair?.player1?.full_name)}</AvatarFallback>
      </Avatar>
      <Avatar className="h-7 w-7 border-2 border-background">
        <AvatarImage src={pair?.player2?.avatar_url || undefined} />
        <AvatarFallback className="text-[10px] bg-primary/10">{getInitials(pair?.player2?.full_name)}</AvatarFallback>
      </Avatar>
    </div>
    <span className="text-xs text-muted-foreground truncate">#{pair?.ladder_rank}</span>
  </div>
);

export const ChallengeMatchesWidget: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { groups, pendingCount, isLoading } = useMyChallengeSuggestions();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  React.useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on('select', onSelect);
    return () => { api.off('select', onSelect); };
  }, [api]);

  const refresh = (groupId: string) => {
    queryClient.invalidateQueries({ queryKey: MY_CHALLENGES_KEY(user?.id) });
    queryClient.invalidateQueries({ queryKey: ['challenges', groupId] });
  };

  const sendChallenge = async (group: GroupChallengeGroup, challengerPairId: string, challengedPairId: string) => {
    if (!user) return;
    const key = `${challengerPairId}:${challengedPairId}`;
    setBusyId(key);
    try {
      const expiresAt = new Date(Date.now() + group.expiryHours * 60 * 60 * 1000).toISOString();
      const { error } = await supabase.from('pair_challenge_matches').insert({
        group_id: group.groupId,
        challenger_pair_id: challengerPairId,
        challenged_pair_id: challengedPairId,
        challenge_type: 'manual',
        challenger_accepted: true,
        points_multiplier: group.pointsMultiplier,
        expires_at: expiresAt,
        created_by: user.id,
      });
      if (error) throw error;
      toast.success('Challenge sent! ⚔️');
      refresh(group.groupId);
    } catch (err) {
      console.error('Error sending challenge from home widget:', err);
      toast.error('Failed to send challenge');
    } finally {
      setBusyId(null);
    }
  };

  const respond = async (group: GroupChallengeGroup, challenge: ChallengeMatch, accepted: boolean) => {
    if (!user) return;
    setBusyId(challenge.id);
    try {
      if (!accepted) {
        const { error } = await supabase
          .from('pair_challenge_matches')
          .update({ status: 'declined', updated_at: new Date().toISOString() })
          .eq('id', challenge.id);
        if (error) throw error;
        toast.info('Challenge declined');
      } else {
        const isPlayer1 = challenge.challenged_pair?.player1_id === user.id;
        const field = isPlayer1 ? 'challenged_player1_accepted' : 'challenged_player2_accepted';
        const { data: updated, error } = await supabase
          .from('pair_challenge_matches')
          .update({ [field]: true, updated_at: new Date().toISOString() })
          .eq('id', challenge.id)
          .select()
          .single();
        if (error) throw error;

        const otherAccepted = isPlayer1
          ? (updated as any).challenged_player2_accepted === true
          : (updated as any).challenged_player1_accepted === true;

        if (otherAccepted) {
          await supabase
            .from('pair_challenge_matches')
            .update({ status: 'accepted', updated_at: new Date().toISOString() })
            .eq('id', challenge.id);
          toast.success('Challenge accepted! 🎯 Time to play!');
        } else {
          toast.success('You accepted! Waiting for your partner...');
        }
      }
      refresh(group.groupId);
    } catch (err) {
      console.error('Error responding to challenge from home widget:', err);
      toast.error('Failed to respond');
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center">
            <Swords className="h-4 w-4 mr-2 text-primary" />
            Challenge Matches
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  if (groups.length === 0) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base flex items-center">
              <Swords className="h-4 w-4 mr-2 text-primary" />
              Challenge Matches
            </CardTitle>
            {pendingCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {pendingCount} awaiting you
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Carousel setApi={setApi} opts={{ align: 'start' }}>
            <CarouselContent>
              {groups.map(group => (
                <CarouselItem key={group.groupId}>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium truncate">{group.groupName}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-primary text-xs h-8"
                        onClick={() => navigate(`/groups/${group.groupId}/stats?tab=challenges`)}
                      >
                        View all <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>

                    {group.pendingForMe.map(challenge => (
                      <div key={challenge.id} className="rounded-lg border border-border p-3 space-y-2 bg-card">
                        <div className="flex items-center gap-1.5">
                          <Swords className="h-3.5 w-3.5 text-primary" />
                          <span className="text-xs font-medium">You've been challenged</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <PairAvatars pair={challenge.challenger_pair} />
                          <span className="text-xs text-muted-foreground shrink-0">vs</span>
                          <PairAvatars pair={challenge.challenged_pair} />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1 min-h-[44px]"
                            disabled={busyId === challenge.id}
                            onClick={() => respond(group, challenge, true)}
                          >
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 min-h-[44px]"
                            disabled={busyId === challenge.id}
                            onClick={() => respond(group, challenge, false)}
                          >
                            Decline
                          </Button>
                        </div>
                      </div>
                    ))}

                    {group.suggestions.map((s, idx) => {
                      const key = `${s.challengerPair.id}:${s.challengedPair.id}`;
                      return (
                        <div key={`${key}-${idx}`} className="rounded-lg border border-border p-3 space-y-2 bg-card">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5">
                              <Zap className="h-3.5 w-3.5 text-primary" />
                              <span className="text-xs font-medium">Suggested challenge</span>
                            </div>
                            <div className="flex gap-1">
                              {s.reason === 'inactive' && (
                                <Badge variant="outline" className="text-xs px-1.5">
                                  <Clock className="h-3 w-3 mr-0.5" />Inactive
                                </Badge>
                              )}
                              <Badge variant="secondary" className="text-xs px-1.5">{s.rankGap} rank gap</Badge>
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <PairAvatars pair={s.challengerPair} />
                            <span className="text-xs text-muted-foreground shrink-0">vs</span>
                            <PairAvatars pair={s.challengedPair} />
                          </div>
                          <Button
                            size="sm"
                            className="w-full min-h-[44px]"
                            disabled={busyId === key}
                            onClick={() => sendChallenge(group, s.challengerPair.id, s.challengedPair.id)}
                          >
                            <Swords className="h-3.5 w-3.5 mr-1.5" />
                            Challenge for {group.pointsMultiplier}x points
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {groups.length > 1 && (
              <>
                <CarouselPrevious className="hidden lg:flex -left-3" />
                <CarouselNext className="hidden lg:flex -right-3" />
              </>
            )}
          </Carousel>

          {groups.length > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-3">
              {groups.map((g, i) => (
                <button
                  key={g.groupId}
                  aria-label={`Show challenges for ${g.groupName}`}
                  onClick={() => api?.scrollTo(i)}
                  className={`h-2 rounded-full transition-all ${i === current ? 'w-5 bg-primary' : 'w-2 bg-muted'}`}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};
